import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  ButtonGroup,
  Divider,
  Alert,
  TextField,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  CircularProgress,
  Tooltip,
  IconButton,
  InputAdornment
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import Collapse from '@mui/material/Collapse';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CommentIcon from '@mui/icons-material/Comment';
import PendingActionsIcon from '@mui/icons-material/PendingActions';

// Import new AI mapping components
import OmopAgentInfoPanel from './OmopAgentInfoPanel';
import OmopCriticAgentPanel from './OmopCriticAgentPanel';
import OmopMappingActions from './OmopMappingActions';
import OmopVocabularySearch from './OmopVocabularySearch';
import OmopSuggestionsTable from './OmopSuggestionsTable';
import { getParameterLookupId } from '../../services/catalogService';
import { useTranslation } from 'react-i18next';
import {
  getMappingProvenance,
  getMappingProvenanceLabel,
  getMappingProvenanceColor,
  OMOP_MAPPING_SOURCE_AI,
  OMOP_MAPPING_SOURCE_IMPORT,
} from '../../pages/olymp/data_catalog/utils/helpers';

// Import mock service
import mockMappingService from '../../services/mockMappingService';
import {
  createManualMapping,
  submitMappingDecision,
} from '../../services/catalogService';
// Import vocabulary data
import omopVocabulary from '../../data/omopVocabulary.json';

const OMOP_VOCABULARY_LOOKUP = (() => {
  const lookup = new Map();
  const concepts = Array.isArray(omopVocabulary?.concepts) ? omopVocabulary.concepts : [];

  concepts.forEach((concept) => {
    if (!concept || typeof concept !== 'object') {
      return;
    }

    const conceptId = String(concept.conceptId ?? '').trim();
    const conceptCode = String(concept.conceptCode ?? '').trim();

    if (conceptId) {
      lookup.set(conceptId, concept);
    }
    if (conceptCode) {
      lookup.set(conceptCode, concept);
    }
  });

  return lookup;
})();

function normalizeOmopConcept(concept) {
  if (!concept || typeof concept !== 'object') {
    return {
      conceptId: '',
      conceptCode: '',
      conceptName: '',
      vocabulary: '',
      domain: '',
      score: null,
      matchScore: null,
    };
  }

  const resolvedScore =
    concept.matchScore
    ?? concept.score
    ?? concept.confidenceScore
    ?? null;

  const rawConceptId = concept.conceptId ?? concept.concept_id ?? concept.id ?? '';
  const rawConceptCode =
    concept.conceptCode
    ?? concept.concept_code
    ?? concept.code
    ?? rawConceptId;

  const vocabularyEntry =
    OMOP_VOCABULARY_LOOKUP.get(String(rawConceptId ?? '').trim())
    || OMOP_VOCABULARY_LOOKUP.get(String(rawConceptCode ?? '').trim())
    || null;

  return {
    ...concept,
    conceptId: rawConceptId,
    conceptCode: rawConceptCode ?? '',
    conceptName:
      concept.conceptName
      ?? concept.concept_name
      ?? concept.name
      ?? vocabularyEntry?.conceptName
      ?? '',
    vocabulary: concept.vocabulary ?? vocabularyEntry?.vocabulary ?? '',
    domain: concept.domain ?? vocabularyEntry?.domain ?? '',
    score: resolvedScore,
    matchScore: resolvedScore,
  };
}

function normalizeProposalEntry(proposal) {
  if (!proposal || typeof proposal !== 'object') {
    return null;
  }

  return {
    ...proposal,
    concept: normalizeOmopConcept(proposal.concept || proposal),
    proposedBy: proposal.proposedBy || proposal.suggestedBy || proposal.createdBy || 'Unknown',
    proposedAt:
      proposal.proposedAt
      || proposal.suggestedAt
      || proposal.createdAt
      || proposal.timestamp
      || null,
    justification: proposal.justification || proposal.reason || '',
    likes: proposal.likes || 0,
    dislikes: proposal.dislikes || 0,
    likedBy: Array.isArray(proposal.likedBy) ? proposal.likedBy : [],
    dislikedBy: Array.isArray(proposal.dislikedBy) ? proposal.dislikedBy : [],
    comments: Array.isArray(proposal.comments) ? proposal.comments : [],
  };
}

function normalizeHistoryEntry(entry) {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  return {
    ...entry,
    concept: normalizeOmopConcept(entry.concept || entry),
    version: entry.version ?? entry.id ?? 'history',
    isCurrent: Boolean(entry.isCurrent),
    changedBy:
      entry.changedBy
      || entry.user
      || entry.approvedBy
      || entry.rejectedBy
      || 'Unknown',
    changedAt:
      entry.changedAt
      || entry.timestamp
      || entry.updatedAt
      || entry.createdAt
      || null,
    changeJustification: entry.changeJustification || entry.reason || entry.justification || '',
    likes: entry.likes || 0,
    dislikes: entry.dislikes || 0,
    likedBy: Array.isArray(entry.likedBy) ? entry.likedBy : [],
    dislikedBy: Array.isArray(entry.dislikedBy) ? entry.dislikedBy : [],
    comments: Array.isArray(entry.comments) ? entry.comments : [],
  };
}

const OMOP_MAPPING_DETAIL_COPY = {
  de: {
    status: {
      validated: 'Validiert',
      mapped: 'Gemappt',
      pending: 'Ausstehend',
      unmapped: 'Nicht gemappt',
    },
    decisionStatus: {
      accepted: 'Freigegeben',
      rejected: 'Abgelehnt',
      deferred: 'Zurückgestellt',
    },
    decisionBy: '{{status}} von {{user}} • {{date}}',
    snackbars: {
      generated: 'AI Mapping erfolgreich generiert!',
      generateError: 'Fehler beim Generieren des Mappings',
      genericError: 'Ein Fehler ist aufgetreten',
      validated: 'Mapping erfolgreich validiert!',
      validateError: 'Fehler beim Validieren',
      rejected: 'Mapping abgelehnt',
      rejectError: 'Fehler beim Ablehnen',
      deferred: 'Entscheidung zurückgestellt',
      decisionRolledBack: 'Entscheidung nicht angekommen -- vorheriger Status wiederhergestellt',
      criticProcessed: 'Critic Agent Feedback verarbeitet!',
      criticError: 'Fehler beim Verarbeiten des Feedbacks',
      alternativeSelected: 'Alternative "{{name}}" ausgewählt!',
      selectError: 'Fehler beim Auswählen',
      saved: '"{{name}}" gespeichert',
      unsaved: '"{{name}}" nicht mehr gespeichert',
      saveError: 'Fehler beim Speichern',
      manualSaved: 'Manuelles Mapping gespeichert!',
      changeReasonRequired: 'Bitte wählen Sie einen Grund für die Änderung',
      justificationRequired: 'Bitte geben Sie eine Begründung an',
      alternativeRequired: 'Bitte wählen Sie ein alternatives Concept',
      changeSubmitted: 'Änderungsvorschlag erfolgreich eingereicht!',
      conceptIdRequired: 'Bitte geben Sie mindestens eine Concept ID ein!',
      manualSubmitted: 'Manueller Vorschlag erfolgreich eingereicht!',
    },
    title: 'OMOP Mapping',
    omopInfoTooltip: 'Was ist OMOP und warum mappen wir?',
    chariteParameter: 'Charité Parameter',
    unknownParameter: 'Unbekannter Parameter',
    omopConcept: 'OMOP Concept',
    confidenceTooltip: 'KI-Konfidenz: Wie sicher ist die KI bei diesem Vorschlag?',
    semanticMatch: 'Semantische Übereinstimmung',
    standardVocabulary: '{{vocabulary}} Standard',
    domainLabel: 'Domain: {{domain}}',
    conceptIdTooltip: 'Eindeutige OMOP-Identifikationsnummer des Konzepts',
    domainTooltip: 'OMOP-Domäne: Kategorisiert das Konzept (z.B. Measurement für Laborwerte, Condition für Diagnosen)',
    vocabularyTooltip: 'Ursprungsvokabular des Konzepts (z.B. LOINC für Labortests, SNOMED für klinische Begriffe)',
    athenaTooltip: 'ATHENA öffnen für detaillierte Konzept-Informationen, Hierarchie und Synonyme',
    link: 'Link',
    workflow: {
      unmapped: 'Starten Sie mit der KI-generierten Mapping-Empfehlung',
      pending: 'Prüfen Sie das vorgeschlagene Mapping',
      mapped: 'Validieren Sie das Mapping oder geben Sie Feedback zur Verbesserung',
      validated: 'Dieses Mapping wurde validiert',
      rejected: 'Generieren Sie ein neues Mapping oder schlagen Sie ein alternatives Konzept vor',
    },
    currentMapping: 'Aktuelles Mapping',
    version: 'Version',
    versions: 'Versionen',
    communityInfo: 'Was bedeuten Likes, Dislikes und Kommentare?',
    validatedBy: '✓ Validiert von {{user}} • {{date}}',
    changedBy: 'Geändert von {{user}} • {{date}}',
    aiGenerated: '• 🤖 KI-generiert',
    addComment: 'Kommentar hinzufügen...',
    comment: 'Kommentieren',
    hideHistory: 'Verlauf ausblenden',
    showHistory: 'Verlauf anzeigen ({{count}} {{label}})',
    noMapping: 'Für diesen Parameter existiert noch kein OMOP Mapping.',
    proposedConcepts: 'Vorgeschlagene Konzepte',
    proposedBy: 'Vorgeschlagen von {{user}} • {{date}}',
    approve: 'Genehmigen',
    reject: 'Ablehnen',
    aiSuggestions: 'KI-Vorschläge',
    candidates: '{{count}} Kandidaten',
    aiSuggestionsHelp: 'Passende OMOP Concepts, nach Relevanz gerankt. Speichern Sie Vorschläge mit 📌, um sie auch bei neuen KI-Läufen beizubehalten.',
    searchPlaceholder: 'Suche nach Concept Name, ID, Code oder Vocabulary...',
    aiAssistant: 'KI-Assistent',
    improveSuggestions: 'Bessere Vorschläge generieren',
    improveInfo: 'Wie funktionieren KI-Vorschläge?',
    improveDescription: 'Beschreiben Sie genauer, wonach Sie suchen. Die KI analysiert Ihr Feedback und generiert neue passende OMOP Concepts mit verbessertem Ranking.',
    feedbackPlaceholder: 'Beschreiben Sie, was Sie suchen oder warum die aktuellen Vorschläge nicht passen...',
    generatingSuggestions: 'KI generiert neue Vorschläge...',
    generateSuggestions: 'Neue Vorschläge generieren',
    manualProposalIntro: 'Oder manuell ein Mapping vorschlagen (z.B. über Concept ID)',
    createManualProposal: 'Manuellen Vorschlag erstellen',
    newManualProposal: 'Neuer manueller Mapping-Vorschlag',
    manualProposalHelp: 'Geben Sie die Details des OMOP Concepts ein, das Sie vorschlagen möchten.',
    conceptId: 'Concept ID *',
    conceptIdPlaceholder: 'z.B. 3004249',
    conceptName: 'Concept Name',
    conceptNamePlaceholder: 'z.B. Systolic blood pressure',
    vocabulary: 'Vocabulary',
    vocabularyPlaceholder: 'z.B. LOINC, SNOMED',
    domain: 'Domain',
    domainPlaceholder: 'z.B. Measurement, Observation',
    justification: 'Begründung',
    justificationPlaceholder: 'Warum schlagen Sie dieses Concept vor?',
    submitProposal: 'Vorschlag einreichen',
    previousImprovements: 'Bisherige Verbesserungen',
    showDetails: 'Details anzeigen',
    improvementSingle: 'Verbesserung',
    improvementPlural: 'Verbesserungen',
    previousImprovementsSummary: 'Sie haben bereits {{count}} {{label}} durchgeführt.',
    improveDialogTitle: 'Vorschlag verbessern',
    close: 'Schließen',
    vocabularySearchTitle: 'OMOP Vocabulary Suche',
    howAiWorks: 'Wie funktionieren KI-Vorschläge?',
    aiIntro: 'Das Multi-Agent-System durchläuft mehrere Schritte, um passende OMOP Concepts zu finden:',
    aiStep1Title: '🔍 Schritt 1: Semantische Analyse',
    aiStep1Body: 'Die KI analysiert den Parameternamen, Tags, Kategorien und Kontext (z.B. ICU, Labor). Sie erstellt eine strukturierte Beschreibung des klinischen Konzepts.',
    aiStep2Title: '🔎 Schritt 2: OMOP Vocabulary Suche',
    aiStep2Body: 'Basierend auf der Analyse werden gezielte Suchanfragen in der lokalen OMOP Vocabulary Datenbank ausgeführt (LOINC, SNOMED, RxNorm, etc.). Die KI findet 8-15 potenzielle Kandidaten.',
    aiStep3Title: '📊 Schritt 3: Ranking & Scoring',
    aiStep3Body: 'Alle gefundenen Concepts werden bewertet und nach Relevanz gerankt. Das Scoring berücksichtigt: Namensübereinstimmung, Vocabulary-Standards, Domain-Passung und klinischen Kontext.',
    usageTips: '💡 Tipps zur Nutzung:',
    tipHigh: 'Hohe Scores (>90%): Meist korrekt, können direkt validiert werden',
    tipMedium: 'Mittlere Scores (70-90%): Sollten manuell geprüft werden',
    tipLow: 'Niedrige Scores (<70%): Nutzen Sie den KI-Assistent für verbesserte Vorschläge',
    tipPin: '📌 Pin-Funktion: Speichern Sie gute Vorschläge, um sie bei neuen KI-Läufen beizubehalten',
    betterSuggestions: '✨ Bessere Vorschläge mit dem KI-Assistent:',
    betterSuggestionsBody: 'Der KI-Assistent verfeinert die Suche basierend auf Ihrem Feedback. Je präziser Sie beschreiben, was Sie suchen, desto besser werden die neuen Vorschläge.',
    helpfulExamples: 'Beispiele für hilfreiches Feedback:',
    commentsTitle: 'Diskussion ({{count}})',
    noComments: 'Noch keine Kommentare vorhanden.',
    historyTitle: 'Mapping-Verlauf',
    currentVersion: '✅ Aktuell',
    changedByLabel: 'Geändert von:',
    reasonLabel: 'Grund:',
    noHistory: 'Kein Verlauf verfügbar.',
    omopInfoTitle: 'Was ist OMOP Mapping?',
    whyMap: 'Warum mappen wir Charité-Parameter zu OMOP?',
    conceptQuestion: 'Was ist ein OMOP Concept?',
    yourRole: 'Ihre Rolle beim Mapping',
    understood: 'Verstanden',
    communityRating: 'Community-Bewertung',
    communityIntro: 'Das OMOP Mapping ist ein kollaborativer Prozess. Ihre Bewertungen helfen dem gesamten Team:',
    likesTitle: 'Likes (Zustimmung)',
    likesBody: 'Zeigen Sie, dass ein Mapping korrekt und gut gewählt ist. Mappings mit vielen Likes werden für andere Nutzer priorisiert angezeigt.',
    dislikesTitle: 'Dislikes (Ablehnung)',
    dislikesBody: 'Markieren Sie problematische oder falsche Mappings. Dies signalisiert anderen Nutzern, dass eine Überprüfung notwendig ist.',
    commentsBodyTitle: 'Kommentare (Diskussion)',
    commentsBody: 'Teilen Sie Ihre medizinische Expertise: Warum ist ein Mapping gut oder problematisch? Welche Alternative wäre besser? Ihre Kommentare helfen Kollegen bei der Entscheidung.',
    communityTip: 'Tipp: Je mehr das Team sich beteiligt, desto schneller entstehen hochqualitative, validierte Mappings für alle Parameter.',
  },
  en: {
    status: {
      validated: 'Validated',
      mapped: 'Mapped',
      pending: 'Pending',
      unmapped: 'Not mapped',
    },
    decisionStatus: {
      accepted: 'Accepted',
      rejected: 'Rejected',
      deferred: 'Deferred',
    },
    decisionBy: '{{status}} by {{user}} • {{date}}',
    snackbars: {
      generated: 'AI mapping generated successfully.',
      generateError: 'Error while generating the mapping',
      genericError: 'An error occurred',
      validated: 'Mapping validated successfully.',
      validateError: 'Error while validating',
      rejected: 'Mapping rejected',
      rejectError: 'Error while rejecting',
      deferred: 'Decision deferred',
      decisionRolledBack: 'Decision did not go through -- previous status restored',
      criticProcessed: 'Critic agent feedback processed.',
      criticError: 'Error while processing feedback',
      alternativeSelected: 'Selected alternative "{{name}}".',
      selectError: 'Error while selecting',
      saved: 'Saved "{{name}}".',
      unsaved: 'Removed "{{name}}" from saved suggestions.',
      saveError: 'Error while saving',
      manualSaved: 'Manual mapping saved.',
      changeReasonRequired: 'Please choose a reason for the change',
      justificationRequired: 'Please provide a justification',
      alternativeRequired: 'Please choose an alternative concept',
      changeSubmitted: 'Change proposal submitted successfully.',
      conceptIdRequired: 'Please enter at least one concept ID.',
      manualSubmitted: 'Manual proposal submitted successfully.',
    },
    title: 'OMOP mapping',
    omopInfoTooltip: 'What is OMOP and why do we map?',
    chariteParameter: 'Charité parameter',
    unknownParameter: 'Unknown parameter',
    omopConcept: 'OMOP concept',
    confidenceTooltip: 'AI confidence: how certain is the AI about this suggestion?',
    semanticMatch: 'Semantic match',
    standardVocabulary: '{{vocabulary}} standard',
    domainLabel: 'Domain: {{domain}}',
    conceptIdTooltip: 'Unique OMOP identification number for the concept',
    domainTooltip: 'OMOP domain: categorizes the concept (e.g. Measurement for lab values, Condition for diagnoses)',
    vocabularyTooltip: 'Source vocabulary of the concept (e.g. LOINC for lab tests, SNOMED for clinical terms)',
    athenaTooltip: 'Open ATHENA for detailed concept information, hierarchy, and synonyms',
    link: 'Link',
    workflow: {
      unmapped: 'Start with the AI-generated mapping recommendation',
      pending: 'Review the proposed mapping',
      mapped: 'Validate the mapping or give feedback to improve it',
      validated: 'This mapping has been validated',
      rejected: 'Generate a new mapping or suggest an alternative concept',
    },
    currentMapping: 'Current mapping',
    version: 'version',
    versions: 'versions',
    communityInfo: 'What do likes, dislikes, and comments mean?',
    validatedBy: '✓ Validated by {{user}} • {{date}}',
    changedBy: 'Changed by {{user}} • {{date}}',
    aiGenerated: '• 🤖 AI-generated',
    addComment: 'Add comment...',
    comment: 'Comment',
    hideHistory: 'Hide history',
    showHistory: 'Show history ({{count}} {{label}})',
    noMapping: 'No OMOP mapping exists yet for this parameter.',
    proposedConcepts: 'Proposed concepts',
    proposedBy: 'Proposed by {{user}} • {{date}}',
    approve: 'Approve',
    reject: 'Reject',
    aiSuggestions: 'AI suggestions',
    candidates: '{{count}} candidates',
    aiSuggestionsHelp: 'Matching OMOP concepts, ranked by relevance. Save suggestions with 📌 to keep them in future AI runs.',
    searchPlaceholder: 'Search by concept name, ID, code, or vocabulary...',
    aiAssistant: 'AI assistant',
    improveSuggestions: 'Generate better suggestions',
    improveInfo: 'How do AI suggestions work?',
    improveDescription: 'Describe more precisely what you are looking for. The AI analyzes your feedback and generates new matching OMOP concepts with improved ranking.',
    feedbackPlaceholder: 'Describe what you are looking for or why the current suggestions are not a good fit...',
    generatingSuggestions: 'AI is generating new suggestions...',
    generateSuggestions: 'Generate new suggestions',
    manualProposalIntro: 'Or propose a mapping manually (e.g. via concept ID)',
    createManualProposal: 'Create manual proposal',
    newManualProposal: 'New manual mapping proposal',
    manualProposalHelp: 'Enter the details of the OMOP concept you want to propose.',
    conceptId: 'Concept ID *',
    conceptIdPlaceholder: 'e.g. 3004249',
    conceptName: 'Concept name',
    conceptNamePlaceholder: 'e.g. Systolic blood pressure',
    vocabulary: 'Vocabulary',
    vocabularyPlaceholder: 'e.g. LOINC, SNOMED',
    domain: 'Domain',
    domainPlaceholder: 'e.g. Measurement, Observation',
    justification: 'Justification',
    justificationPlaceholder: 'Why are you proposing this concept?',
    submitProposal: 'Submit proposal',
    previousImprovements: 'Previous improvements',
    showDetails: 'Show details',
    improvementSingle: 'improvement',
    improvementPlural: 'improvements',
    previousImprovementsSummary: 'You have already completed {{count}} {{label}}.',
    improveDialogTitle: 'Improve suggestion',
    close: 'Close',
    vocabularySearchTitle: 'OMOP vocabulary search',
    howAiWorks: 'How do AI suggestions work?',
    aiIntro: 'The multi-agent system goes through several steps to find suitable OMOP concepts:',
    aiStep1Title: '🔍 Step 1: Semantic analysis',
    aiStep1Body: 'The AI analyzes the parameter name, tags, categories, and context (e.g. ICU, lab). It creates a structured description of the clinical concept.',
    aiStep2Title: '🔎 Step 2: OMOP vocabulary search',
    aiStep2Body: 'Based on the analysis, targeted searches are executed in the local OMOP vocabulary database (LOINC, SNOMED, RxNorm, etc.). The AI finds 8-15 potential candidates.',
    aiStep3Title: '📊 Step 3: Ranking & scoring',
    aiStep3Body: 'All found concepts are evaluated and ranked by relevance. The scoring considers name similarity, vocabulary standards, domain fit, and clinical context.',
    usageTips: '💡 Usage tips:',
    tipHigh: 'High scores (>90%): Usually correct and can often be validated directly',
    tipMedium: 'Medium scores (70-90%): Should be reviewed manually',
    tipLow: 'Low scores (<70%): Use the AI assistant for improved suggestions',
    tipPin: '📌 Pin feature: Save good suggestions so they remain available in future AI runs',
    betterSuggestions: '✨ Better suggestions with the AI assistant:',
    betterSuggestionsBody: 'The AI assistant refines the search based on your feedback. The more precisely you describe what you need, the better the new suggestions will be.',
    helpfulExamples: 'Examples of helpful feedback:',
    commentsTitle: 'Discussion ({{count}})',
    noComments: 'No comments available yet.',
    historyTitle: 'Mapping history',
    currentVersion: '✅ Current',
    changedByLabel: 'Changed by:',
    reasonLabel: 'Reason:',
    noHistory: 'No history available.',
    omopInfoTitle: 'What is OMOP mapping?',
    whyMap: 'Why do we map Charité parameters to OMOP?',
    conceptQuestion: 'What is an OMOP concept?',
    yourRole: 'Your role in mapping',
    understood: 'Understood',
    communityRating: 'Community rating',
    communityIntro: 'OMOP mapping is a collaborative process. Your ratings help the whole team:',
    likesTitle: 'Likes (agreement)',
    likesBody: 'Show that a mapping is correct and well chosen. Mappings with many likes are shown with higher priority for other users.',
    dislikesTitle: 'Dislikes (rejection)',
    dislikesBody: 'Mark problematic or incorrect mappings. This signals to other users that a review is needed.',
    commentsBodyTitle: 'Comments (discussion)',
    commentsBody: 'Share your medical expertise: why is a mapping good or problematic? Which alternative would be better? Your comments help colleagues decide.',
    communityTip: 'Tip: the more the team participates, the faster highly qualified, validated mappings are created for all parameters.',
  },
  pt: {
    status: {
      validated: 'Validado',
      mapped: 'Mapeado',
      pending: 'Pendente',
      unmapped: 'Não mapeado',
    },
    decisionStatus: {
      accepted: 'Aprovado',
      rejected: 'Rejeitado',
      deferred: 'Adiado',
    },
    decisionBy: '{{status}} por {{user}} • {{date}}',
    snackbars: {
      generated: 'Mapeamento IA gerado com sucesso.',
      generateError: 'Erro ao gerar o mapeamento',
      genericError: 'Ocorreu um erro',
      validated: 'Mapeamento validado com sucesso.',
      validateError: 'Erro ao validar',
      rejected: 'Mapeamento rejeitado',
      rejectError: 'Erro ao rejeitar',
      deferred: 'Decisão adiada',
      decisionRolledBack: 'A decisão não foi guardada -- estado anterior restaurado',
      criticProcessed: 'Feedback do agente crítico processado.',
      criticError: 'Erro ao processar o feedback',
      alternativeSelected: 'Alternativa "{{name}}" selecionada.',
      selectError: 'Erro ao selecionar',
      saved: '"{{name}}" guardado.',
      unsaved: '"{{name}}" removido das sugestões guardadas.',
      saveError: 'Erro ao guardar',
      manualSaved: 'Mapeamento manual guardado.',
      changeReasonRequired: 'Selecione um motivo para a alteração',
      justificationRequired: 'Introduza uma justificação',
      alternativeRequired: 'Selecione um conceito alternativo',
      changeSubmitted: 'Proposta de alteração enviada com sucesso.',
      conceptIdRequired: 'Introduza pelo menos um concept ID.',
      manualSubmitted: 'Proposta manual enviada com sucesso.',
    },
    title: 'Mapeamento OMOP',
    omopInfoTooltip: 'O que é OMOP e porque fazemos o mapeamento?',
    chariteParameter: 'Parâmetro Charité',
    unknownParameter: 'Parâmetro desconhecido',
    omopConcept: 'Conceito OMOP',
    confidenceTooltip: 'Confiança da IA: quão segura está a IA sobre esta sugestão?',
    semanticMatch: 'Correspondência semântica',
    standardVocabulary: 'Padrão {{vocabulary}}',
    domainLabel: 'Domínio: {{domain}}',
    conceptIdTooltip: 'Número de identificação OMOP único do conceito',
    domainTooltip: 'Domínio OMOP: categoriza o conceito (ex.: Measurement para valores laboratoriais, Condition para diagnósticos)',
    vocabularyTooltip: 'Vocabulário de origem do conceito (ex.: LOINC para testes laboratoriais, SNOMED para termos clínicos)',
    athenaTooltip: 'Abrir ATHENA para informação detalhada do conceito, hierarquia e sinónimos',
    link: 'Ligação',
    workflow: {
      unmapped: 'Comece com a recomendação de mapeamento gerada pela IA',
      pending: 'Reveja o mapeamento proposto',
      mapped: 'Valide o mapeamento ou dê feedback para o melhorar',
      validated: 'Este mapeamento foi validado',
      rejected: 'Gere um novo mapeamento ou proponha um conceito alternativo',
    },
    currentMapping: 'Mapeamento atual',
    version: 'versão',
    versions: 'versões',
    communityInfo: 'O que significam gostos, rejeições e comentários?',
    validatedBy: '✓ Validado por {{user}} • {{date}}',
    changedBy: 'Alterado por {{user}} • {{date}}',
    aiGenerated: '• 🤖 Gerado por IA',
    addComment: 'Adicionar comentário...',
    comment: 'Comentar',
    hideHistory: 'Ocultar histórico',
    showHistory: 'Mostrar histórico ({{count}} {{label}})',
    noMapping: 'Ainda não existe mapeamento OMOP para este parâmetro.',
    proposedConcepts: 'Conceitos propostos',
    proposedBy: 'Proposto por {{user}} • {{date}}',
    approve: 'Aprovar',
    reject: 'Rejeitar',
    aiSuggestions: 'Sugestões da IA',
    candidates: '{{count}} candidatos',
    aiSuggestionsHelp: 'Conceitos OMOP adequados, classificados por relevância. Guarde sugestões com 📌 para as manter em futuras execuções da IA.',
    searchPlaceholder: 'Pesquisar por nome do conceito, ID, código ou vocabulário...',
    aiAssistant: 'Assistente IA',
    improveSuggestions: 'Gerar sugestões melhores',
    improveInfo: 'Como funcionam as sugestões da IA?',
    improveDescription: 'Descreva com mais precisão o que procura. A IA analisa o seu feedback e gera novos conceitos OMOP adequados com melhor classificação.',
    feedbackPlaceholder: 'Descreva o que procura ou porque as sugestões atuais não são adequadas...',
    generatingSuggestions: 'A IA está a gerar novas sugestões...',
    generateSuggestions: 'Gerar novas sugestões',
    manualProposalIntro: 'Ou proponha manualmente um mapeamento (ex.: através do concept ID)',
    createManualProposal: 'Criar proposta manual',
    newManualProposal: 'Nova proposta manual de mapeamento',
    manualProposalHelp: 'Introduza os detalhes do conceito OMOP que pretende propor.',
    conceptId: 'Concept ID *',
    conceptIdPlaceholder: 'ex.: 3004249',
    conceptName: 'Nome do conceito',
    conceptNamePlaceholder: 'ex.: Systolic blood pressure',
    vocabulary: 'Vocabulário',
    vocabularyPlaceholder: 'ex.: LOINC, SNOMED',
    domain: 'Domínio',
    domainPlaceholder: 'ex.: Measurement, Observation',
    justification: 'Justificação',
    justificationPlaceholder: 'Porque propõe este conceito?',
    submitProposal: 'Enviar proposta',
    previousImprovements: 'Melhorias anteriores',
    showDetails: 'Mostrar detalhes',
    improvementSingle: 'melhoria',
    improvementPlural: 'melhorias',
    previousImprovementsSummary: 'Já realizou {{count}} {{label}}.',
    improveDialogTitle: 'Melhorar sugestão',
    close: 'Fechar',
    vocabularySearchTitle: 'Pesquisa no vocabulário OMOP',
    howAiWorks: 'Como funcionam as sugestões da IA?',
    aiIntro: 'O sistema multiagente passa por várias etapas para encontrar conceitos OMOP adequados:',
    aiStep1Title: '🔍 Etapa 1: Análise semântica',
    aiStep1Body: 'A IA analisa o nome do parâmetro, tags, categorias e contexto (ex.: ICU, laboratório). Cria uma descrição estruturada do conceito clínico.',
    aiStep2Title: '🔎 Etapa 2: Pesquisa no vocabulário OMOP',
    aiStep2Body: 'Com base na análise, são executadas pesquisas direcionadas na base de dados local do vocabulário OMOP (LOINC, SNOMED, RxNorm, etc.). A IA encontra 8-15 candidatos potenciais.',
    aiStep3Title: '📊 Etapa 3: Ranking e pontuação',
    aiStep3Body: 'Todos os conceitos encontrados são avaliados e classificados por relevância. A pontuação considera semelhança do nome, padrões do vocabulário, adequação do domínio e contexto clínico.',
    usageTips: '💡 Dicas de utilização:',
    tipHigh: 'Pontuações altas (>90%): Normalmente corretas e podem muitas vezes ser validadas diretamente',
    tipMedium: 'Pontuações médias (70-90%): Devem ser revistas manualmente',
    tipLow: 'Pontuações baixas (<70%): Use o assistente IA para sugestões melhores',
    tipPin: '📌 Função de pin: Guarde boas sugestões para as manter em futuras execuções da IA',
    betterSuggestions: '✨ Sugestões melhores com o assistente IA:',
    betterSuggestionsBody: 'O assistente IA refina a pesquisa com base no seu feedback. Quanto mais precisamente descrever o que precisa, melhores serão as novas sugestões.',
    helpfulExamples: 'Exemplos de feedback útil:',
    commentsTitle: 'Discussão ({{count}})',
    noComments: 'Ainda não existem comentários.',
    historyTitle: 'Histórico de mapeamento',
    currentVersion: '✅ Atual',
    changedByLabel: 'Alterado por:',
    reasonLabel: 'Motivo:',
    noHistory: 'Nenhum histórico disponível.',
    omopInfoTitle: 'O que é o mapeamento OMOP?',
    whyMap: 'Porque mapeamos os parâmetros da Charité para OMOP?',
    conceptQuestion: 'O que é um conceito OMOP?',
    yourRole: 'O seu papel no mapeamento',
    understood: 'Compreendido',
    communityRating: 'Avaliação da comunidade',
    communityIntro: 'O mapeamento OMOP é um processo colaborativo. As suas avaliações ajudam toda a equipa:',
    likesTitle: 'Gostos (concordância)',
    likesBody: 'Mostre que um mapeamento está correto e foi bem escolhido. Mapeamentos com muitos gostos são mostrados com maior prioridade a outros utilizadores.',
    dislikesTitle: 'Rejeições (discordância)',
    dislikesBody: 'Marque mapeamentos problemáticos ou incorretos. Isto sinaliza a outros utilizadores que é necessária uma revisão.',
    commentsBodyTitle: 'Comentários (discussão)',
    commentsBody: 'Partilhe a sua experiência médica: porque é que um mapeamento é bom ou problemático? Que alternativa seria melhor? Os seus comentários ajudam os colegas na decisão.',
    communityTip: 'Dica: quanto mais a equipa participa, mais rapidamente surgem mapeamentos altamente qualificados e validados para todos os parâmetros.',
  },
};

const OMOP_MAPPING_DETAIL_EXAMPLES = {
  de: [
    'Ich suche speziell systolischen Blutdruck in sitzender Position',
    'Das Konzept sollte aus SNOMED statt LOINC sein',
    'Zu allgemein - ich brauche ein Konzept für pädiatrische Patienten',
    'Die Domain sollte Observation statt Measurement sein',
  ],
  en: [
    'I am specifically looking for systolic blood pressure in a seated position',
    'The concept should come from SNOMED instead of LOINC',
    'Too general - I need a concept for pediatric patients',
    'The domain should be Observation instead of Measurement',
  ],
  pt: [
    'Procuro especificamente pressão arterial sistólica em posição sentada',
    'O conceito deve vir de SNOMED em vez de LOINC',
    'É demasiado geral - preciso de um conceito para doentes pediátricos',
    'O domínio deve ser Observation em vez de Measurement',
  ],
};

const OMOP_INFO_CONTENT = {
  de: {
    intro: 'OMOP (Observational Medical Outcomes Partnership) ist ein internationaler Standard für die Harmonisierung klinischer Daten aus verschiedenen Quellen.',
    whyMap: [
      'Interoperabilität: Ermöglicht den Austausch von Daten mit anderen Kliniken und Forschungseinrichtungen',
      'Standardisierung: Einheitliche Begriffe statt klinikeigener Terminologie',
      'Forschung: Vereinfacht Multi-Center-Studien und Big-Data-Analysen',
      'Qualität: Reduziert Fehler durch eindeutige Zuordnungen',
    ],
    concept: 'Ein OMOP Concept ist eine eindeutige Repräsentation eines medizinischen Begriffs (z.B. ein Laborwert, eine Diagnose, ein Medikament). Jedes Concept hat eine eindeutige ID und gehört zu einem standardisierten Vokabular (z.B. LOINC für Laborwerte, SNOMED für klinische Begriffe).',
    role: 'Als medizinischer Experte validieren Sie KI-generierte Mappings oder schlagen bessere Alternativen vor. Ihre Expertise ist entscheidend für die Qualität und Genauigkeit der Datenstandardisierung.',
  },
  en: {
    intro: 'OMOP (Observational Medical Outcomes Partnership) is an international standard for harmonizing clinical data from different sources.',
    whyMap: [
      'Interoperability: Enables data exchange with other hospitals and research institutions',
      'Standardization: Consistent terminology instead of hospital-specific labels',
      'Research: Simplifies multi-center studies and big-data analyses',
      'Quality: Reduces errors through unambiguous mappings',
    ],
    concept: 'An OMOP concept is a unique representation of a medical term (e.g. a lab value, diagnosis, or medication). Each concept has a unique ID and belongs to a standardized vocabulary (e.g. LOINC for lab values, SNOMED for clinical terms).',
    role: 'As a medical expert, you validate AI-generated mappings or suggest better alternatives. Your expertise is essential for the quality and accuracy of data standardization.',
  },
  pt: {
    intro: 'OMOP (Observational Medical Outcomes Partnership) é um padrão internacional para harmonizar dados clínicos de diferentes fontes.',
    whyMap: [
      'Interoperabilidade: Permite a troca de dados com outros hospitais e instituições de investigação',
      'Padronização: Termos consistentes em vez de terminologia específica de cada hospital',
      'Investigação: Simplifica estudos multicêntricos e análises de big data',
      'Qualidade: Reduz erros através de mapeamentos inequívocos',
    ],
    concept: 'Um conceito OMOP é uma representação única de um termo médico (por ex. um valor laboratorial, diagnóstico ou medicamento). Cada conceito tem um ID único e pertence a um vocabulário padronizado (por ex. LOINC para valores laboratoriais, SNOMED para termos clínicos).',
    role: 'Como especialista médico, valida mapeamentos gerados por IA ou propõe melhores alternativas. A sua experiência é essencial para a qualidade e precisão da normalização dos dados.',
  },
};

/**
 * OmopMappingDetail Component
 * Comprehensive OMOP mapping section with AI Multi-Agent System integration
 * Shows detailed OMOP information, AI agent outputs, critic agent, and vocabulary browser
 *
 * @param {object} props
 * @param {object} props.parameter - Full parameter object with omopMapping data
 * @param {function} props.onMappingUpdate - Callback when mapping is updated
 * @param {string} props.userRole - Current user's role (user, data_steward, admin)
 */
const OmopMappingDetail = ({
  parameter,
  onMappingUpdate,
  userRole = 'user',
  onCreateProposal,
  onApproveProposal,
  onRejectProposal,
  onVoteProposal,
  onCommentProposal,
  onVoteCurrentMapping,
  onCommentCurrentMapping,
  currentUserName = 'Current User',
}) => {
  const { t, i18n } = useTranslation();
  // Debug: Log the current user role
  console.log('OmopMappingDetail - Current userRole:', userRole);
  const language = i18n.resolvedLanguage?.startsWith('pt')
    ? 'pt'
    : i18n.resolvedLanguage?.startsWith('en')
      ? 'en'
      : 'de';
  const copy = OMOP_MAPPING_DETAIL_COPY[language];
  const locale = language === 'pt' ? 'pt-PT' : language === 'en' ? 'en-US' : 'de-DE';
  const exampleFeedback = OMOP_MAPPING_DETAIL_EXAMPLES[language];
  const omopInfo = OMOP_INFO_CONTENT[language];

  // Extract mapping data from parameter
  const omopMapping = parameter?.omopMapping || {};
  const omopStatus = omopMapping.status || 'unmapped';
  const topMatch = omopMapping.topMatch ? normalizeOmopConcept(omopMapping.topMatch) : null;
  const aiMetadata = omopMapping.aiMetadata;
  const agent3Output = aiMetadata?.agent3Output;
  const actorIdentifier = currentUserName || 'Current User';
  const alternatives = Array.isArray(omopMapping.alternatives)
    ? omopMapping.alternatives.map(normalizeOmopConcept)
    : [];
  const validationHistory = omopMapping.validationHistory || [];
  const criticHistory = omopMapping.criticHistory || [];
  // Die zuletzt getroffene Freigabe-Entscheidung (#99) -- accepted/rejected/deferred
  // mit optionaler Notiz. Erkennbar bleibt sie hier, aenderbar ueber die Buttons
  // in OmopMappingActions, die unabhaengig vom Wert weiter aktiv bleiben.
  const mappingDecision = omopMapping.decision || null;
  // Herkunft der Zuordnung (#257): importiert / KI-Vorschlag / vom Menschen
  // validiert -- ausgeschrieben in der Detailansicht (AK1). `parameter` traegt
  // die vom Katalog-Service transformierten Felder (`omopStatus`,
  // `omopMappingSource`); `null`, wenn die API dazu nichts sagt (AK5).
  const mappingProvenance = getMappingProvenance(parameter);
  const mappingProvenanceLabel = getMappingProvenanceLabel(mappingProvenance, t);

  // Community & Collaboration data
  const communityLikes = omopMapping.communityLikes || 0;
  const communityDislikes = omopMapping.communityDislikes || 0;
  const proposedMappings = Array.isArray(omopMapping.proposedMappings)
    ? omopMapping.proposedMappings.map(normalizeProposalEntry).filter(Boolean)
    : [];
  console.log('OmopMappingDetail - proposedMappings count:', proposedMappings.length);
  console.log('OmopMappingDetail - Show approve/reject buttons?', userRole === 'data_steward' || userRole === 'admin');
  const comments = omopMapping.comments || [];
  const mappingHistory = Array.isArray(omopMapping.mappingHistory)
    ? omopMapping.mappingHistory.map(normalizeHistoryEntry).filter(Boolean)
    : [];

  // State management
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCriticDialog, setShowCriticDialog] = useState(false);
  const [showVocabSearchDialog, setShowVocabSearchDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [currentStage, setCurrentStage] = useState(null);
  const [feedback, setFeedback] = useState('');

  // Pin management - track user-pinned concepts
  const [pinnedConceptIds, setPinnedConceptIds] = useState(
    omopMapping.userPinnedConcepts?.map(p => p.conceptId) || []
  );
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [showFeedbackSection, setShowFeedbackSection] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showChangeRequestDialog, setShowChangeRequestDialog] = useState(false);
  const [showCommentsDialog, setShowCommentsDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);

  // Change request form state
  const [changeRequestReason, setChangeRequestReason] = useState('');
  const [changeRequestJustification, setChangeRequestJustification] = useState('');
  const [selectedAlternativeConcept, setSelectedAlternativeConcept] = useState(null);
  const [showVocabSearchInDialog, setShowVocabSearchInDialog] = useState(false);

  // Proposal interaction state
  const [showProposalCommentInput, setShowProposalCommentInput] = useState({});
  const [proposalCommentText, setProposalCommentText] = useState({});

  // Mapping interaction state (for current + history)
  const [showMappingCommentInput, setShowMappingCommentInput] = useState({});
  const [mappingCommentText, setMappingCommentText] = useState({});

  // History collapse state
  const [showMappingHistory, setShowMappingHistory] = useState(false);

  // Info dialogs
  const [showCommunityInfoDialog, setShowCommunityInfoDialog] = useState(false);
  const [showOmopInfoDialog, setShowOmopInfoDialog] = useState(false);

  // Manual proposal form state
  const [showManualProposalForm, setShowManualProposalForm] = useState(false);
  const [manualProposalData, setManualProposalData] = useState({
    conceptId: '',
    conceptName: '',
    vocabulary: '',
    domain: '',
    justification: ''
  });

  // Calculate mapping quality from AI metadata or validation history
  const mappingQuality = {
    score: aiMetadata?.confidenceScore
      ? Math.round(aiMetadata.confidenceScore * 100)
      : (omopStatus === 'validated' ? 95 : omopStatus === 'mapped' ? 75 : 0),
    confidence: aiMetadata?.confidenceScore >= 0.9 ? 'Very high'
      : aiMetadata?.confidenceScore >= 0.7 ? 'High'
      : aiMetadata?.confidenceScore >= 0.5 ? 'Medium'
      : 'Low',
    validatedBy: validationHistory[0]?.user || null,
    validatedDate: validationHistory[0]?.timestamp || null,
    mappedBy: aiMetadata ? 'AI Multi-Agent System' : 'Manuell',
    mappedDate: omopMapping.mappedDate || new Date().toISOString()
  };

  // Action Handlers
  const handleGenerateMapping = async () => {
    setIsProcessing(true);
    setCurrentStage(null);

    try {
      const result = await mockMappingService.generateAiMapping(
        parameter,
        (stageName, status, data) => {
          setCurrentStage({ name: stageName, status, ...data });
        }
      );

      if (result.status === 'success') {
        onMappingUpdate && onMappingUpdate({
          ...parameter,
          omopStatus: 'mapped',
          omopMapping: result.omopMapping
        });
        setSnackbar({
          open: true,
          message: copy.snackbars.generated,
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: copy.snackbars.generateError,
          severity: 'error'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: copy.snackbars.genericError,
        severity: 'error'
      });
    } finally {
      setIsProcessing(false);
      setCurrentStage(null);
    }
  };

  // Das zweite Gate: der Mensch entscheidet ueber den Mapping-Vorschlag (#99).
  // Die Anzeige wird sofort optimistisch aktualisiert, damit sich die Aktion
  // sofort anfuehlt; kommt die Entscheidung nicht an, geht die Anzeige auf den
  // Stand vor dem Klick zurueck und der Nutzer sieht eine Fehlermeldung --
  // ohne diese Ruecknahme waere optimistisches Aktualisieren gefaehrlicher als
  // gar keins.
  const handleMappingDecision = async (decision, note = '') => {
    const previousParameter = parameter;
    const optimisticStatus = decision === 'accepted' ? 'validated' : omopStatus;
    const optimisticDecision = {
      status: decision,
      note: note || '',
      decidedBy: actorIdentifier,
      decidedAt: new Date().toISOString(),
    };

    setIsProcessing(true);
    onMappingUpdate && onMappingUpdate({
      ...parameter,
      omopStatus: optimisticStatus,
      omopMapping: {
        ...omopMapping,
        status: optimisticStatus,
        decision: optimisticDecision,
      },
    });

    try {
      const result = await submitMappingDecision(parameter.id, decision, note);

      if (result.source === 'api' && result.parameter) {
        onMappingUpdate && onMappingUpdate(result.parameter);
        setSnackbar({
          open: true,
          message: copy.snackbars[decision] || copy.snackbars.validated,
          severity: decision === 'rejected' ? 'info' : 'success'
        });
      }
    } catch (error) {
      onMappingUpdate && onMappingUpdate(previousParameter);
      setSnackbar({
        open: true,
        message: copy.snackbars.decisionRolledBack,
        severity: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleValidate = (note) => handleMappingDecision('accepted', note);
  const handleReject = (note) => handleMappingDecision('rejected', note);
  const handleDefer = (note) => handleMappingDecision('deferred', note);

  const handleCriticFeedback = async (feedback) => {
    setIsProcessing(true);

    try {
      const result = await mockMappingService.submitCriticFeedback(
        parameter.id,
        feedback,
        omopMapping
      );

      if (result.status === 'success') {
        onMappingUpdate && onMappingUpdate({
          ...parameter,
          omopMapping: result.omopMapping
        });

        setSnackbar({
          open: true,
          message: copy.snackbars.criticProcessed,
          severity: 'success'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: copy.snackbars.criticError,
        severity: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSelectAlternative = async (candidate) => {
    setIsProcessing(true);

    try {
      // Update mapping with selected alternative as new top match
      const updatedMapping = {
        ...omopMapping,
        status: 'mapped',
        topMatch: {
          conceptId: candidate.conceptId,
          conceptCode: candidate.conceptCode || candidate.conceptId,
          conceptName: candidate.conceptName,
          vocabulary: candidate.vocabulary,
          domain: candidate.domain || 'Measurement',
          // candidate kommt aus aiCandidates = agent3Output.allCandidates.map(normalizeOmopConcept)
          // (Zeile 717-719) -- dort steht die Begruendung als `reason`, nicht `rationale`, und
          // `stage` fehlt ganz, weil jeder Eintrag per Konstruktion Agent-3-Output ist. Ohne
          // diesen Fallback verschwindet der Stufen-Chip, sobald eine Alternative aus dieser
          // Liste ausgewaehlt wird (#100 Rueckmeldung Runde 1, Blocker B5).
          stage: candidate.stage || 'agent3',
          rationale: candidate.rationale || candidate.reason || null
        },
        aiMetadata: {
          ...aiMetadata,
          confidenceScore: candidate.score || 0.85,
          // Keep agent outputs but update top candidate
          agent3Output: {
            ...agent3Output,
            allCandidates: agent3Output?.allCandidates
          }
        }
      };

      onMappingUpdate && onMappingUpdate({
        ...parameter,
        omopStatus: 'mapped',
        omopMapping: updatedMapping
      });

      setSnackbar({
        open: true,
        message: copy.snackbars.alternativeSelected.replace('{{name}}', candidate.conceptName),
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: copy.snackbars.selectError,
        severity: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTogglePin = async (candidate) => {
    const conceptId = candidate.conceptId;
    const isPinned = pinnedConceptIds.includes(conceptId);

    try {
      let updatedPinnedIds;
      let updatedPinnedConcepts;

      if (isPinned) {
        // Unpin
        updatedPinnedIds = pinnedConceptIds.filter(id => id !== conceptId);
        updatedPinnedConcepts = (omopMapping.userPinnedConcepts || []).filter(
          p => p.conceptId !== conceptId
        );
      } else {
        // Pin
        updatedPinnedIds = [...pinnedConceptIds, conceptId];
        updatedPinnedConcepts = [
          ...(omopMapping.userPinnedConcepts || []),
          {
            conceptId: candidate.conceptId,
            conceptCode: candidate.conceptCode,
            conceptName: candidate.conceptName,
            vocabulary: candidate.vocabulary,
            domain: candidate.domain,
            score: candidate.score,
            pinnedAt: new Date().toISOString(),
            pinnedBy: actorIdentifier
          }
        ];
      }

      setPinnedConceptIds(updatedPinnedIds);

      // Update parent component
      const updatedMapping = {
        ...omopMapping,
        userPinnedConcepts: updatedPinnedConcepts
      };

      onMappingUpdate && onMappingUpdate({
        ...parameter,
        omopMapping: updatedMapping
      });

      setSnackbar({
        open: true,
        message: isPinned
          ? copy.snackbars.unsaved.replace('{{name}}', candidate.conceptName)
          : copy.snackbars.saved.replace('{{name}}', candidate.conceptName),
        severity: 'info'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: copy.snackbars.saveError,
        severity: 'error'
      });
    }
  };

  const handleManualMapping = async (concept) => {
    setIsProcessing(true);

    try {
      const result = await createManualMapping(parameter.id, concept);

      if (result.source === 'api' && result.parameter) {
        onMappingUpdate && onMappingUpdate(result.parameter);
        setShowVocabSearchDialog(false);
        setSnackbar({
          open: true,
          message: copy.snackbars.manualSaved,
          severity: 'success'
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: copy.snackbars.saveError,
        severity: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmitChangeRequest = async () => {
    // Validate form
    if (!changeRequestReason) {
      setSnackbar({
        open: true,
        message: copy.snackbars.changeReasonRequired,
        severity: 'warning'
      });
      return;
    }

    if (!changeRequestJustification.trim()) {
      setSnackbar({
        open: true,
        message: copy.snackbars.justificationRequired,
        severity: 'warning'
      });
      return;
    }

    if (!selectedAlternativeConcept) {
      setSnackbar({
        open: true,
        message: copy.snackbars.alternativeRequired,
        severity: 'warning'
      });
      return;
    }

    // Create new proposal
    const proposalPayload = {
      ...selectedAlternativeConcept,
      justification: changeRequestJustification,
      metadata: {
        changeRequestReason,
      },
    };

    if (onCreateProposal) {
      const createdParameter = await onCreateProposal(proposalPayload);
      if (!createdParameter) {
        setSnackbar({
          open: true,
          message: copy.snackbars.genericError,
          severity: 'error'
        });
        return false;
      }
    } else {
      const newProposal = {
        id: `prop_${Date.now()}`,
        concept: selectedAlternativeConcept,
        proposedBy: actorIdentifier,
        proposedAt: new Date().toISOString(),
        reason: changeRequestReason,
        justification: changeRequestJustification,
        votes: 0,
        status: 'pending_review'
      };

      const updatedMapping = {
        ...omopMapping,
        proposedMappings: [...proposedMappings, newProposal]
      };

      onMappingUpdate && onMappingUpdate({
        ...parameter,
        omopMapping: updatedMapping
      });
    }

    // Reset form and close dialog
    setChangeRequestReason('');
    setChangeRequestJustification('');
    setSelectedAlternativeConcept(null);
    setShowVocabSearchInDialog(false);
    setShowChangeRequestDialog(false);

    setSnackbar({
      open: true,
      message: copy.snackbars.changeSubmitted,
      severity: 'success'
    });
    return true;
  };

  // Handle proposal submission from table
  const handleSubmitProposalFromTable = async ({ candidate, justification }) => {
    const normalizedCandidate = normalizeOmopConcept(candidate);
    const proposalPayload = {
      conceptId: normalizedCandidate.conceptId,
      conceptCode: normalizedCandidate.conceptCode || normalizedCandidate.conceptId,
      conceptName: normalizedCandidate.conceptName,
      vocabulary: normalizedCandidate.vocabulary,
      domain: normalizedCandidate.domain || 'Measurement',
      justification: justification || '',
    };

    if (!proposalPayload.conceptId || !proposalPayload.conceptName) {
      setSnackbar({
        open: true,
        message: copy.snackbars.genericError,
        severity: 'error'
      });
      return false;
    }

    if (onCreateProposal) {
      const createdParameter = await onCreateProposal(proposalPayload);
      if (!createdParameter) {
        setSnackbar({
          open: true,
          message: copy.snackbars.genericError,
          severity: 'error'
        });
        return false;
      }
    } else {
      const newProposal = {
        id: `prop_${Date.now()}`,
        concept: proposalPayload,
        proposedBy: actorIdentifier,
        proposedAt: new Date().toISOString(),
        justification: justification || '',
        votes: 0,
        likes: 0,
        dislikes: 0,
        likedBy: [],
        dislikedBy: [],
        comments: [],
        status: 'pending_review'
      };

      const updatedMapping = {
        ...omopMapping,
        proposedMappings: [...proposedMappings, newProposal]
      };

      onMappingUpdate && onMappingUpdate({
        ...parameter,
        omopMapping: updatedMapping
      });
    }

    setSnackbar({
      open: true,
      message: 'Änderungsvorschlag erfolgreich eingereicht!',
      severity: 'success'
    });
    return true;
  };

  // Handle manual proposal submission
  const handleSubmitManualProposal = async () => {
    // Validation
    if (!manualProposalData.conceptId.trim()) {
      setSnackbar({
        open: true,
        message: copy.snackbars.conceptIdRequired,
        severity: 'error'
      });
      return;
    }

    const proposalPayload = {
      conceptId: manualProposalData.conceptId.trim(),
      conceptCode: manualProposalData.conceptId.trim(),
      conceptName: manualProposalData.conceptName.trim() || `Concept ${manualProposalData.conceptId}`,
      vocabulary: manualProposalData.vocabulary.trim() || 'Unknown',
      domain: manualProposalData.domain.trim() || 'Measurement',
      justification: manualProposalData.justification.trim() || 'Manuell vorgeschlagen',
    };

    if (onCreateProposal) {
      const createdParameter = await onCreateProposal(proposalPayload);
      if (!createdParameter) {
        setSnackbar({
          open: true,
          message: copy.snackbars.genericError,
          severity: 'error'
        });
        return;
      }
    } else {
      const newProposal = {
        id: `prop_manual_${Date.now()}`,
        concept: {
          ...proposalPayload,
        },
        proposedBy: actorIdentifier,
        proposedAt: new Date().toISOString(),
        justification: proposalPayload.justification,
        votes: 0,
        likes: 0,
        dislikes: 0,
        likedBy: [],
        dislikedBy: [],
        comments: [],
        status: 'pending_review'
      };

      const updatedMapping = {
        ...omopMapping,
        proposedMappings: [...proposedMappings, newProposal]
      };

      onMappingUpdate && onMappingUpdate({
        ...parameter,
        omopMapping: updatedMapping
      });
    }

    // Reset form
    setManualProposalData({
      conceptId: '',
      conceptName: '',
      vocabulary: '',
      domain: '',
      justification: ''
    });
    setShowManualProposalForm(false);

    setSnackbar({
      open: true,
      message: copy.snackbars.manualSubmitted,
      severity: 'success'
    });
  };

  const handleApproveProposal = async (proposalId) => {
    if (!proposalId) return;

    if (onApproveProposal) {
      await onApproveProposal(proposalId);
      return;
    }

    const acceptedProposal = proposedMappings.find(proposal => proposal.id === proposalId);
    if (!acceptedProposal) return;

    onMappingUpdate && onMappingUpdate({
      ...parameter,
      omopStatus: 'validated',
      omopMapping: {
        ...omopMapping,
        status: 'validated',
        topMatch: acceptedProposal.concept,
        proposedMappings: proposedMappings.filter(proposal => proposal.id !== proposalId),
      },
    });
  };

  const handleRejectProposal = async (proposalId) => {
    if (!proposalId) return;

    if (onRejectProposal) {
      await onRejectProposal(proposalId);
      return;
    }

    onMappingUpdate && onMappingUpdate({
      ...parameter,
      omopMapping: {
        ...omopMapping,
        proposedMappings: proposedMappings.filter(proposal => proposal.id !== proposalId),
      },
    });
  };

  // Handle like/dislike on proposals
  const handleLikeProposal = async (proposalId) => {
    if (onVoteProposal) {
      await onVoteProposal(proposalId, 'like');
      return;
    }

    const updatedProposals = proposedMappings.map(p => {
      if (p.id === proposalId) {
        const alreadyLiked = p.likedBy?.includes(actorIdentifier);
        const alreadyDisliked = p.dislikedBy?.includes(actorIdentifier);

        if (alreadyLiked) return p;

        return {
          ...p,
          likes: (p.likes || 0) + 1,
          likedBy: [...(p.likedBy || []), actorIdentifier],
          dislikes: alreadyDisliked ? (p.dislikes || 0) - 1 : (p.dislikes || 0),
          dislikedBy: alreadyDisliked ? (p.dislikedBy || []).filter(u => u !== actorIdentifier) : (p.dislikedBy || [])
        };
      }
      return p;
    });

    onMappingUpdate && onMappingUpdate({
      ...parameter,
      omopMapping: { ...omopMapping, proposedMappings: updatedProposals }
    });
  };

  const handleDislikeProposal = async (proposalId) => {
    if (onVoteProposal) {
      await onVoteProposal(proposalId, 'dislike');
      return;
    }

    const updatedProposals = proposedMappings.map(p => {
      if (p.id === proposalId) {
        const alreadyLiked = p.likedBy?.includes(actorIdentifier);
        const alreadyDisliked = p.dislikedBy?.includes(actorIdentifier);

        if (alreadyDisliked) return p;

        return {
          ...p,
          dislikes: (p.dislikes || 0) + 1,
          dislikedBy: [...(p.dislikedBy || []), actorIdentifier],
          likes: alreadyLiked ? (p.likes || 0) - 1 : (p.likes || 0),
          likedBy: alreadyLiked ? (p.likedBy || []).filter(u => u !== actorIdentifier) : (p.likedBy || [])
        };
      }
      return p;
    });

    onMappingUpdate && onMappingUpdate({
      ...parameter,
      omopMapping: { ...omopMapping, proposedMappings: updatedProposals }
    });
  };

  const handleAddProposalComment = async (proposalId) => {
    const commentText = proposalCommentText[proposalId];
    if (!commentText || !commentText.trim()) return;

    if (onCommentProposal) {
      await onCommentProposal(proposalId, commentText.trim());
      setProposalCommentText({ ...proposalCommentText, [proposalId]: '' });
      setShowProposalCommentInput({ ...showProposalCommentInput, [proposalId]: false });
      return;
    }

    const updatedProposals = proposedMappings.map(p => {
      if (p.id === proposalId) {
        const newComment = {
          id: Date.now(),
          author: actorIdentifier,
          text: commentText.trim(),
          timestamp: new Date().toISOString()
        };
        return {
          ...p,
          comments: [...(p.comments || []), newComment]
        };
      }
      return p;
    });

    onMappingUpdate && onMappingUpdate({
      ...parameter,
      omopMapping: { ...omopMapping, proposedMappings: updatedProposals }
    });

    setProposalCommentText({ ...proposalCommentText, [proposalId]: '' });
    setShowProposalCommentInput({ ...showProposalCommentInput, [proposalId]: false });
  };

  // Handle like/dislike on mappings (current or history)
  const handleLikeMapping = async (mappingId) => {
    if (mappingId === 'current' && onVoteCurrentMapping) {
      await onVoteCurrentMapping('like');
      return;
    }

    const updatedHistory = mappingHistory.map(m => {
      if (m.version === mappingId || (m.isCurrent && mappingId === 'current')) {
        const alreadyLiked = m.likedBy?.includes(actorIdentifier);
        const alreadyDisliked = m.dislikedBy?.includes(actorIdentifier);

        if (alreadyLiked) return m;

        return {
          ...m,
          likes: (m.likes || 0) + 1,
          likedBy: [...(m.likedBy || []), actorIdentifier],
          dislikes: alreadyDisliked ? (m.dislikes || 0) - 1 : (m.dislikes || 0),
          dislikedBy: alreadyDisliked ? (m.dislikedBy || []).filter(u => u !== actorIdentifier) : (m.dislikedBy || [])
        };
      }
      return m;
    });

    onMappingUpdate && onMappingUpdate({
      ...parameter,
      omopMapping: { ...omopMapping, mappingHistory: updatedHistory }
    });
  };

  const handleDislikeMapping = async (mappingId) => {
    if (mappingId === 'current' && onVoteCurrentMapping) {
      await onVoteCurrentMapping('dislike');
      return;
    }

    const updatedHistory = mappingHistory.map(m => {
      if (m.version === mappingId || (m.isCurrent && mappingId === 'current')) {
        const alreadyLiked = m.likedBy?.includes(actorIdentifier);
        const alreadyDisliked = m.dislikedBy?.includes(actorIdentifier);

        if (alreadyDisliked) return m;

        return {
          ...m,
          dislikes: (m.dislikes || 0) + 1,
          dislikedBy: [...(m.dislikedBy || []), actorIdentifier],
          likes: alreadyLiked ? (m.likes || 0) - 1 : (m.likes || 0),
          likedBy: alreadyLiked ? (m.likedBy || []).filter(u => u !== actorIdentifier) : (m.likedBy || [])
        };
      }
      return m;
    });

    onMappingUpdate && onMappingUpdate({
      ...parameter,
      omopMapping: { ...omopMapping, mappingHistory: updatedHistory }
    });
  };

  const handleAddMappingComment = async (mappingId) => {
    const commentText = mappingCommentText[mappingId];
    if (!commentText || !commentText.trim()) return;

    if (mappingId === 'current' && onCommentCurrentMapping) {
      await onCommentCurrentMapping(commentText.trim());
      setMappingCommentText({ ...mappingCommentText, [mappingId]: '' });
      setShowMappingCommentInput({ ...showMappingCommentInput, [mappingId]: false });
      return;
    }

    const updatedHistory = mappingHistory.map(m => {
      if (m.version === mappingId || (m.isCurrent && mappingId === 'current')) {
        const newComment = {
          id: Date.now(),
          author: actorIdentifier,
          text: commentText.trim(),
          timestamp: new Date().toISOString()
        };
        return {
          ...m,
          comments: [...(m.comments || []), newComment]
        };
      }
      return m;
    });

    onMappingUpdate && onMappingUpdate({
      ...parameter,
      omopMapping: { ...omopMapping, mappingHistory: updatedHistory }
    });

    setMappingCommentText({ ...mappingCommentText, [mappingId]: '' });
    setShowMappingCommentInput({ ...showMappingCommentInput, [mappingId]: false });
  };

  const getStatusIcon = () => {
    switch (omopStatus) {
      case 'validated':
        return <CheckCircleIcon color="success" />;
      case 'mapped':
        return <InfoIcon color="info" />;
      case 'pending':
        return <WarningIcon color="warning" />;
      default:
        return <WarningIcon color="error" />;
    }
  };

  const getStatusLabel = () => {
    switch (omopStatus) {
      case 'validated':
        return copy.status.validated;
      case 'mapped':
        return copy.status.mapped;
      case 'pending':
        return copy.status.pending;
      default:
        return copy.status.unmapped;
    }
  };

  const getStatusColor = () => {
    switch (omopStatus) {
      case 'validated':
        return 'success';
      case 'mapped':
        return 'info';
      case 'pending':
        return 'warning';
      default:
        return 'error';
    }
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* OMOP Mapping (Combined View) */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            {/* Header with Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              {getStatusIcon()}
              <Typography variant="h6" fontWeight="bold">
                {copy.title}
              </Typography>
              <Tooltip title={copy.omopInfoTooltip}>
                <IconButton
                  size="small"
                  onClick={() => setShowOmopInfoDialog(true)}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Chip
                label={getStatusLabel()}
                color={getStatusColor()}
                size="small"
                sx={{ ml: 'auto' }}
              />
            </Box>

            {/* Charite Parameter */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                {copy.chariteParameter}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {parameter?.name || copy.unknownParameter}
              </Typography>
            </Box>

            {/* Herkunft der Zuordnung (#257 AK1-AK3). Kein Block, wenn die API
                keine Herkunft liefert -- kein erfundener Wert (AK5). */}
            {mappingProvenanceLabel && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                  {t('dataCatalog.mappingProvenance.detailHeading', 'Herkunft')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={mappingProvenanceLabel}
                    color={getMappingProvenanceColor(mappingProvenance)}
                    variant={mappingProvenance === OMOP_MAPPING_SOURCE_AI ? 'outlined' : 'filled'}
                    size="small"
                  />
                  {mappingProvenance === OMOP_MAPPING_SOURCE_AI && (
                    <Typography variant="caption" color="text.secondary">
                      {t('dataCatalog.mappingProvenance.unvalidatedHint', 'Nicht geprüft')}
                    </Typography>
                  )}
                  {/* AK2: Sammlung und Lizenz, wo immer die API mindestens
                      eine von beiden liefert. Bis Runde 1 haengte der ganze
                      Block an `omopMappingSourceCollection` -- eine Lizenz ohne
                      Sammlungsnamen (oder eine, deren Wert vom Namen der
                      Sammlung getrennt eintrifft) verschwand dadurch spurlos,
                      obwohl AK2 die Lizenz ausdruecklich verlangt. */}
                  {mappingProvenance === OMOP_MAPPING_SOURCE_IMPORT
                    && (parameter?.omopMappingSourceCollection || parameter?.omopMappingSourceLicense) && (
                    <Typography variant="caption" color="text.secondary">
                      {parameter?.omopMappingSourceCollection && parameter?.omopMappingSourceLicense
                        ? t('dataCatalog.mappingProvenance.importedFrom', {
                            defaultValue: 'Quelle: {{collection}} ({{license}})',
                            collection: parameter.omopMappingSourceCollection,
                            license: parameter.omopMappingSourceLicense,
                          })
                        : parameter?.omopMappingSourceCollection
                          ? t('dataCatalog.mappingProvenance.importedFromNoLicense', {
                              defaultValue: 'Quelle: {{collection}}',
                              collection: parameter.omopMappingSourceCollection,
                            })
                          : t('dataCatalog.mappingProvenance.importedLicenseOnly', {
                              defaultValue: 'Lizenz: {{license}}',
                              license: parameter.omopMappingSourceLicense,
                            })}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            {topMatch ? (
              <>
                {/* OMOP Concept Overview */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                    {copy.omopConcept}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                    {topMatch.conceptName}
                  </Typography>

                  {/* Matching Score Visualization */}
                  {aiMetadata && (
                    <Box sx={{ mt: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <LinearProgress
                          variant="determinate"
                          value={topMatch.matchScore || aiMetadata.overallConfidence || 85}
                          sx={{
                            flex: 1,
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                              background: (topMatch.matchScore || aiMetadata.overallConfidence || 85) >= 80
                                ? 'linear-gradient(90deg, #4caf50 0%, #66bb6a 100%)'
                                : (topMatch.matchScore || aiMetadata.overallConfidence || 85) >= 60
                                ? 'linear-gradient(90deg, #ff9800 0%, #ffa726 100%)'
                                : 'linear-gradient(90deg, #f44336 0%, #ef5350 100%)'
                            }
                          }}
                        />
                      <Tooltip title={copy.confidenceTooltip}>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            {Math.round(topMatch.matchScore || aiMetadata.overallConfidence || 85)}%
                          </Typography>
                        </Tooltip>
                      </Box>

                      {/* Matching Reasons */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {aiMetadata.matchReasons ? (
                          aiMetadata.matchReasons.map((reason, idx) => (
                            <Chip
                              key={idx}
                              label={reason}
                              size="small"
                              icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                              sx={{
                                height: 24,
                                fontSize: '0.7rem',
                                backgroundColor: '#e8f5e9',
                                color: '#2e7d32',
                                '& .MuiChip-icon': { color: '#4caf50' }
                              }}
                            />
                          ))
                        ) : (
                          <>
                            <Chip
                              label={copy.semanticMatch}
                              size="small"
                              icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                              sx={{
                                height: 24,
                                fontSize: '0.7rem',
                                backgroundColor: '#e8f5e9',
                                color: '#2e7d32',
                                '& .MuiChip-icon': { color: '#4caf50' }
                              }}
                            />
                              <Chip
                                label={copy.standardVocabulary.replace('{{vocabulary}}', topMatch.vocabulary)}
                              size="small"
                              icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                              sx={{
                                height: 24,
                                fontSize: '0.7rem',
                                backgroundColor: '#e8f5e9',
                                color: '#2e7d32',
                                '& .MuiChip-icon': { color: '#4caf50' }
                              }}
                            />
                            {topMatch.domain && (
                              <Chip
                                label={copy.domainLabel.replace('{{domain}}', topMatch.domain)}
                                size="small"
                                icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                                sx={{
                                  height: 24,
                                  fontSize: '0.7rem',
                                  backgroundColor: '#e8f5e9',
                                  color: '#2e7d32',
                                  '& .MuiChip-icon': { color: '#4caf50' }
                                }}
                              />
                            )}
                          </>
                        )}
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Concept Details with Tooltips */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={3}>
                    <Tooltip title={copy.conceptIdTooltip} arrow>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Concept ID
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {topMatch.conceptId}
                        </Typography>
                      </Paper>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={3}>
                    <Tooltip title={copy.domainTooltip} arrow>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Domain
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {topMatch.domain || 'Measurement'}
                        </Typography>
                      </Paper>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={3}>
                    <Tooltip title={copy.vocabularyTooltip} arrow>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Vocabulary
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {topMatch.vocabulary}
                        </Typography>
                      </Paper>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={3}>
                    <Tooltip title={copy.athenaTooltip} arrow>
                      <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          ATHENA
                        </Typography>
                        <Typography
                          variant="body1"
                          fontWeight="bold"
                          component="a"
                          href={`https://athena.ohdsi.org/search-terms/terms/${topMatch.conceptId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          {copy.link}
                        </Typography>
                      </Paper>
                    </Tooltip>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* AI Generation Progress */}
                {currentStage && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {currentStage.displayName}
                    </Typography>
                    <Typography variant="caption">
                      {currentStage.description}
                    </Typography>
                    <LinearProgress sx={{ mt: 1 }} />
                  </Alert>
                )}

                {/* Action Buttons with Workflow Guidance */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                    {omopStatus === 'unmapped' && copy.workflow.unmapped}
                    {omopStatus === 'pending' && copy.workflow.pending}
                    {omopStatus === 'mapped' && copy.workflow.mapped}
                    {omopStatus === 'validated' && copy.workflow.validated}
                    {omopStatus === 'rejected' && copy.workflow.rejected}
                  </Typography>
                  {mappingDecision && (
                    <Alert
                      severity={
                        mappingDecision.status === 'rejected'
                          ? 'warning'
                          : mappingDecision.status === 'deferred'
                            ? 'info'
                            : 'success'
                      }
                      sx={{ mb: 1 }}
                    >
                      {copy.decisionBy
                        .replace('{{status}}', copy.decisionStatus[mappingDecision.status] || mappingDecision.status)
                        .replace('{{user}}', mappingDecision.decidedBy || actorIdentifier)
                        .replace('{{date}}', mappingDecision.decidedAt
                          ? new Date(mappingDecision.decidedAt).toLocaleString(locale)
                          : '')}
                      {mappingDecision.note && (
                        <Typography variant="caption" display="block">
                          {mappingDecision.note}
                        </Typography>
                      )}
                    </Alert>
                  )}
                  <OmopMappingActions
                    omopStatus={omopStatus}
                    onValidate={handleValidate}
                    onReject={handleReject}
                    onDefer={handleDefer}
                    onEdit={() => setShowVocabSearchDialog(true)}
                    onGenerateMapping={handleGenerateMapping}
                    onOpenCritic={() => setShowCriticDialog(true)}
                    isProcessing={isProcessing}
                    hasAiMapping={!!aiMetadata}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Current Mapping with Community Features */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <CheckCircleIcon color="success" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight="bold">
                      {copy.currentMapping}
                    </Typography>
                    {mappingHistory.length > 0 && (
                      <Chip
                        label={`${mappingHistory.length} ${mappingHistory.length > 1 ? copy.versions : copy.version}`}
                        size="small"
                        color="primary"
                      />
                    )}
                    <Tooltip title={copy.communityInfo}>
                      <IconButton
                        size="small"
                        onClick={() => setShowCommunityInfoDialog(true)}
                        sx={{ ml: 'auto' }}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

              {/* Current Mapping - Green Card */}
              {(() => {
                const historyCurrentMapping = mappingHistory.find(m => m.isCurrent);
                const currentMapping = historyCurrentMapping
                  ? {
                      ...historyCurrentMapping,
                      likes: historyCurrentMapping.likes ?? communityLikes ?? 0,
                      dislikes: historyCurrentMapping.dislikes ?? communityDislikes ?? 0,
                      likedBy: historyCurrentMapping.likedBy ?? [],
                      dislikedBy: historyCurrentMapping.dislikedBy ?? [],
                    }
                  : {
                      concept: topMatch,
                      version: 'current',
                      changedBy: mappingQuality.validatedBy || 'System',
                      changedAt: mappingQuality.validatedDate || new Date().toISOString(),
                      isCurrent: true,
                      likes: communityLikes || 0,
                      dislikes: communityDislikes || 0,
                      likedBy: [],
                      dislikedBy: [],
                      comments: []
                    };

                const hasLiked = currentMapping.likedBy?.includes(actorIdentifier);
                const hasDisliked = currentMapping.dislikedBy?.includes(actorIdentifier);

                return (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      mb: 1.5,
                      backgroundColor: '#e8f5e9',
                      borderColor: '#4caf50',
                      border: '2px solid'
                    }}
                  >
                    {/* Header with concept name and interaction buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <CheckCircleIcon fontSize="small" sx={{ color: '#4caf50' }} />
                        <Typography variant="body2" fontWeight="bold">
                          {currentMapping.concept?.conceptName || topMatch.conceptName}
                        </Typography>
                        <Chip
                          label={(currentMapping.concept?.vocabulary || topMatch.vocabulary)}
                          size="small"
                          sx={{ height: 16, fontSize: '0.6rem' }}
                        />
                      </Box>

                      {/* Like/Dislike/Comment buttons */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleLikeMapping('current')}
                            disabled={hasLiked}
                            sx={{ p: 0.3, color: hasLiked ? '#4caf50' : 'inherit' }}
                          >
                            <ThumbUpIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                          <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                            {currentMapping.likes || 0}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleDislikeMapping('current')}
                            disabled={hasDisliked}
                            sx={{ p: 0.3, color: hasDisliked ? '#f44336' : 'inherit' }}
                          >
                            <ThumbDownIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                          <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                            {currentMapping.dislikes || 0}
                          </Typography>
                        </Box>

                        <IconButton
                          size="small"
                          onClick={() =>
                            setShowMappingCommentInput({
                              ...showMappingCommentInput,
                              ['current']: !showMappingCommentInput['current']
                            })
                          }
                          sx={{ p: 0.3 }}
                        >
                          <CommentIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                        </IconButton>

                        <IconButton
                          size="small"
                          component="a"
                          href={`https://athena.ohdsi.org/search-terms/terms/${currentMapping.concept?.conceptId || topMatch.conceptId}`}
                          target="_blank"
                          sx={{ p: 0.3 }}
                        >
                          <SearchIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Box>
                    </Box>

                    <Typography variant="caption" color="textSecondary" display="block">
                      {(currentMapping.concept?.conceptCode || topMatch.conceptCode)} • {(currentMapping.concept?.domain || topMatch.domain || 'Measurement')}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                      sx={{ mb: currentMapping.comments?.length > 0 || showMappingCommentInput['current'] ? 1 : 0 }}
                    >
                      {copy.validatedBy
                        .replace('{{user}}', currentMapping.changedBy)
                        .replace('{{date}}', new Date(currentMapping.changedAt).toLocaleDateString(locale))}
                      {aiMetadata && ` ${copy.aiGenerated}`}
                    </Typography>

                    {/* Comment input */}
                    {showMappingCommentInput['current'] && (
                      <Box sx={{ mt: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder={copy.addComment}
                          value={mappingCommentText['current'] || ''}
                          onChange={e =>
                            setMappingCommentText({
                              ...mappingCommentText,
                              ['current']: e.target.value
                            })
                          }
                          sx={{ mb: 0.5 }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleAddMappingComment('current')}
                          disabled={!mappingCommentText['current']?.trim()}
                        >
                          {copy.comment}
                        </Button>
                      </Box>
                    )}

                    {/* Comments display */}
                    {currentMapping.comments && currentMapping.comments.length > 0 && (
                      <Box sx={{ mt: 1, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
                        {currentMapping.comments.map((comment) => (
                          <Box key={comment.id} sx={{ mb: 0.5 }}>
                            <Typography variant="caption" fontWeight="bold">
                              {comment.author}
                            </Typography>
                            <Typography variant="caption" display="block" color="textSecondary">
                              {comment.text}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                );
              })()}

              {/* History Toggle Button */}
              {mappingHistory.filter(m => !m.isCurrent).length > 0 && (
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Button
                    size="small"
                    startIcon={showMappingHistory ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setShowMappingHistory(!showMappingHistory)}
                    sx={{ textTransform: 'none' }}
                  >
                    {showMappingHistory
                      ? copy.hideHistory
                      : copy.showHistory
                        .replace('{{count}}', mappingHistory.filter(m => !m.isCurrent).length)
                        .replace('{{label}}', mappingHistory.filter(m => !m.isCurrent).length === 1 ? copy.version : copy.versions)}
                  </Button>
                </Box>
              )}

              {/* History Mappings - Collapsible Gray Cards */}
              <Collapse in={showMappingHistory}>
                {mappingHistory.filter(m => !m.isCurrent).reverse().map((historyMapping) => {
                const hasLiked = historyMapping.likedBy?.includes(actorIdentifier);
                const hasDisliked = historyMapping.dislikedBy?.includes(actorIdentifier);
                const historyConcept = normalizeOmopConcept(historyMapping?.concept || historyMapping);

                return (
                  <Paper
                    key={historyMapping.version}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      mb: 1.5,
                      backgroundColor: '#f5f5f5',
                      borderColor: '#e0e0e0',
                      '&:last-child': { mb: 0 }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {historyConcept.conceptName || copy.omopConcept}
                        </Typography>
                        <Chip
                          label={historyConcept.vocabulary || 'Unknown'}
                          size="small"
                          sx={{ height: 16, fontSize: '0.6rem' }}
                        />
                        <Chip
                          label={`v${historyMapping.version}`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 16, fontSize: '0.6rem' }}
                        />
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleLikeMapping(historyMapping.version)}
                            disabled={hasLiked}
                            sx={{ p: 0.3, color: hasLiked ? '#4caf50' : 'inherit' }}
                          >
                            <ThumbUpIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                          <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                            {historyMapping.likes || 0}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleDislikeMapping(historyMapping.version)}
                            disabled={hasDisliked}
                            sx={{ p: 0.3, color: hasDisliked ? '#f44336' : 'inherit' }}
                          >
                            <ThumbDownIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                          <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                            {historyMapping.dislikes || 0}
                          </Typography>
                        </Box>

                        <IconButton
                          size="small"
                          onClick={() =>
                            setShowMappingCommentInput({
                              ...showMappingCommentInput,
                              [historyMapping.version]: !showMappingCommentInput[historyMapping.version]
                            })
                          }
                          sx={{ p: 0.3 }}
                        >
                          <CommentIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                        </IconButton>

                        <IconButton
                          size="small"
                          component="a"
                          href={`https://athena.ohdsi.org/search-terms/terms/${historyConcept.conceptId}`}
                          target="_blank"
                          sx={{ p: 0.3 }}
                        >
                          <SearchIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Box>
                    </Box>

                    <Typography variant="caption" color="textSecondary" display="block">
                      {historyConcept.conceptCode} • {historyConcept.domain}
                    </Typography>

                    {historyMapping.changeJustification && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        {historyMapping.changeJustification}
                      </Typography>
                    )}

                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                      sx={{ mb: historyMapping.comments?.length > 0 || showMappingCommentInput[historyMapping.version] ? 1 : 0 }}
                    >
                      {copy.changedBy
                        .replace('{{user}}', historyMapping.changedBy)
                        .replace('{{date}}', new Date(historyMapping.changedAt).toLocaleDateString(locale))}
                    </Typography>

                    {showMappingCommentInput[historyMapping.version] && (
                      <Box sx={{ mt: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder={copy.addComment}
                          value={mappingCommentText[historyMapping.version] || ''}
                          onChange={e =>
                            setMappingCommentText({
                              ...mappingCommentText,
                              [historyMapping.version]: e.target.value
                            })
                          }
                          sx={{ mb: 0.5 }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleAddMappingComment(historyMapping.version)}
                          disabled={!mappingCommentText[historyMapping.version]?.trim()}
                        >
                          {copy.comment}
                        </Button>
                      </Box>
                    )}

                    {historyMapping.comments && historyMapping.comments.length > 0 && (
                      <Box sx={{ mt: 1, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
                        {historyMapping.comments.map((comment) => (
                          <Box key={comment.id} sx={{ mb: 0.5 }}>
                            <Typography variant="caption" fontWeight="bold">
                              {comment.author}
                            </Typography>
                            <Typography variant="caption" display="block" color="textSecondary">
                              {comment.text}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                );
              })}
              </Collapse>
            </Box>
              </>
            ) : (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    {copy.noMapping}
                  </Typography>
                </Alert>

                {/* AI Generation Progress */}
                {currentStage && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {currentStage.displayName}
                    </Typography>
                    <Typography variant="caption">
                      {currentStage.description}
                    </Typography>
                    <LinearProgress sx={{ mt: 1 }} />
                  </Alert>
                )}

                {/* Action Buttons */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                    {copy.workflow.unmapped}
                  </Typography>
                  <OmopMappingActions
                    omopStatus={omopStatus}
                    onValidate={handleValidate}
                    onReject={handleReject}
                    onDefer={handleDefer}
                    onEdit={() => setShowVocabSearchDialog(true)}
                    onGenerateMapping={handleGenerateMapping}
                    onOpenCritic={() => setShowCriticDialog(true)}
                    isProcessing={isProcessing}
                    hasAiMapping={!!aiMetadata}
                  />
                </Box>
              </>
            )}
          </Paper>
        </Grid>

        {/* Vorgeschlagene Konzepte */}
        {proposedMappings.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PendingActionsIcon color="warning" />
                <Typography variant="h6" fontWeight="bold">
                  {copy.proposedConcepts}
                </Typography>
                <Chip
                  label={proposedMappings.length}
                  size="small"
                  color="warning"
                />
              </Box>

              {proposedMappings.map((proposal) => {
                const hasLiked = proposal.likedBy?.includes(actorIdentifier);
                const hasDisliked = proposal.dislikedBy?.includes(actorIdentifier);
                const proposalConcept = normalizeOmopConcept(proposal?.concept || proposal);

                return (
                  <Paper
                    key={proposal.id}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      mb: 1.5,
                      backgroundColor: '#fff9e6',
                      borderColor: '#ff9800',
                      '&:last-child': { mb: 0 }
                    }}
                  >
                    {/* Header with concept name and interaction buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {proposalConcept.conceptName || copy.omopConcept}
                        </Typography>
                        <Chip
                          label={proposalConcept.vocabulary || 'Unknown'}
                          size="small"
                          sx={{ height: 16, fontSize: '0.6rem' }}
                        />
                      </Box>

                      {/* Like/Dislike/Comment buttons */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleLikeProposal(proposal.id)}
                            disabled={hasLiked}
                            sx={{
                              p: 0.3,
                              color: hasLiked ? '#4caf50' : 'inherit'
                            }}
                          >
                            <ThumbUpIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                          <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                            {proposal.likes || 0}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleDislikeProposal(proposal.id)}
                            disabled={hasDisliked}
                            sx={{
                              p: 0.3,
                              color: hasDisliked ? '#f44336' : 'inherit'
                            }}
                          >
                            <ThumbDownIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                          <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                            {proposal.dislikes || 0}
                          </Typography>
                        </Box>

                        {/* Comment button */}
                        <IconButton
                          size="small"
                          onClick={() =>
                            setShowProposalCommentInput({
                              ...showProposalCommentInput,
                              [proposal.id]: !showProposalCommentInput[proposal.id]
                            })
                          }
                          sx={{ p: 0.3 }}
                        >
                          <CommentIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                        </IconButton>

                        {/* ATHENA Link */}
                        <IconButton
                          size="small"
                          component="a"
                          href={`https://athena.ohdsi.org/search-terms/terms/${proposalConcept.conceptId}`}
                          target="_blank"
                          sx={{ p: 0.3 }}
                        >
                          <SearchIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Concept details */}
                    <Typography variant="caption" color="textSecondary" display="block">
                      {proposalConcept.conceptCode} • {proposalConcept.domain}
                    </Typography>

                    {/* Justification */}
                    {proposal.justification && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        {proposal.justification}
                      </Typography>
                    )}

                    {/* Proposed by */}
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      display="block"
                      sx={{ mb: 1 }}
                    >
                      {copy.proposedBy
                        .replace('{{user}}', proposal.proposedBy)
                        .replace('{{date}}', new Date(proposal.proposedAt).toLocaleDateString(locale))}
                    </Typography>

                    {/* Accept/Reject Proposal Buttons - only for Data Stewards/Admins */}
                    {(userRole === 'data_steward' || userRole === 'admin') && (
                      <Box sx={{ display: 'flex', gap: 0.5, mb: proposal.comments?.length > 0 || showProposalCommentInput[proposal.id] ? 1 : 0 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<CheckIcon />}
                          onClick={() => handleApproveProposal(proposal.id)}
                          sx={{ flex: 1, py: 0.3 }}
                        >
                          {copy.approve}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<CloseIcon />}
                          onClick={() => handleRejectProposal(proposal.id)}
                          sx={{ flex: 1, py: 0.3 }}
                        >
                          {copy.reject}
                        </Button>
                      </Box>
                    )}

                    {/* Comment input (collapsible) */}
                    {showProposalCommentInput[proposal.id] && (
                      <Box sx={{ mt: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder={copy.addComment}
                          value={proposalCommentText[proposal.id] || ''}
                          onChange={e =>
                            setProposalCommentText({
                              ...proposalCommentText,
                              [proposal.id]: e.target.value
                            })
                          }
                          sx={{ mb: 0.5 }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleAddProposalComment(proposal.id)}
                          disabled={!proposalCommentText[proposal.id]?.trim()}
                        >
                          {copy.comment}
                        </Button>
                      </Box>
                    )}

                    {/* Comments display */}
                    {proposal.comments && proposal.comments.length > 0 && (
                      <Box sx={{ mt: 1, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
                        {proposal.comments.map((comment) => (
                          <Box key={comment.id} sx={{ mb: 0.5 }}>
                            <Typography variant="caption" fontWeight="bold">
                              {comment.author}
                            </Typography>
                            <Typography variant="caption" display="block" color="textSecondary">
                              {comment.text}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                );
              })}
            </Paper>
          </Grid>
        )}

        {/* KI-Vorschläge (Table with Pin functionality) -- candidates come from
            GET /api/mapping/candidates via parameterId, not from the mock
            agent3Output. The table itself owns loading/error/empty states.
            Identifier via getParameterLookupId, not a bare parameter.id: the
            same helper catalogService.getParameter() uses for the catalog
            detail endpoint, so a parameter whose stable id lives under
            `parameterId` (not `id`) still shows the candidates section and
            the AK5 empty state instead of silently vanishing here (Rueckmeldung
            Runde 1). */}
        {getParameterLookupId(parameter) && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LeaderboardIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  {copy.aiSuggestions}
                </Typography>
              </Box>

              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                {copy.aiSuggestionsHelp}
              </Typography>

              {/* Wie ist der Vorschlag entstanden? Stufe/Begruendung/Modell/Zeitpunkt aus dem
                  Kandidaten-Contract, nicht aus einer erzeugten Erzaehlung (#100). */}
              <Box sx={{ mb: 3 }}>
                <OmopAgentInfoPanel aiMetadata={aiMetadata} topMatch={topMatch} alternatives={alternatives} />
              </Box>

              {/* Search Bar with KI-Assistent Button */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={copy.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={() => setShowFeedbackSection(!showFeedbackSection)}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5568d3 0%, #6a4193 100%)',
                    },
                    whiteSpace: 'nowrap'
                  }}
                >
                  {copy.aiAssistant}
                </Button>
              </Box>

              {/* Collapsible Feedback Section - Above Table */}
              <Collapse in={showFeedbackSection}>
                <Box sx={{
                  p: 3,
                  mb: 3,
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor: 'primary.light'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <AutoAwesomeIcon color="primary" sx={{ fontSize: 28 }} />
                      <Typography variant="h6" fontWeight="bold">
                        {copy.improveSuggestions}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={copy.improveInfo}>
                        <IconButton size="small" onClick={() => setShowInfoDialog(true)}>
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small" onClick={() => setShowFeedbackSection(false)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    {copy.improveDescription}
                  </Typography>

                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder={copy.feedbackPlaceholder}
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    disabled={isProcessing}
                    sx={{ mb: 2 }}
                    variant="outlined"
                  />
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={isProcessing ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <AutoAwesomeIcon />}
                    onClick={() => {
                      if (feedback.trim()) {
                        handleCriticFeedback(feedback);
                        setFeedback('');
                      }
                    }}
                    disabled={!feedback.trim() || isProcessing}
                    fullWidth
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5568d3 0%, #6a4193 100%)',
                      },
                      py: 1.5
                    }}
                  >
                    {isProcessing ? copy.generatingSuggestions : copy.generateSuggestions}
                  </Button>
                </Box>
              </Collapse>

              <OmopSuggestionsTable
                parameterId={getParameterLookupId(parameter)}
                currentTopMatchId={topMatch?.conceptId}
                pinnedConceptIds={pinnedConceptIds}
                onSelectCandidate={handleSelectAlternative}
                onSubmitProposal={handleSubmitProposalFromTable}
                onTogglePin={handleTogglePin}
                isProcessing={isProcessing}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
              />

              {/* Manual Proposal Form */}
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {copy.manualProposalIntro}
                  </Typography>
                  <Button
                    size="small"
                    variant={showManualProposalForm ? "outlined" : "contained"}
                    color="primary"
                    onClick={() => setShowManualProposalForm(!showManualProposalForm)}
                  >
                    {showManualProposalForm ? copy.cancel : copy.createManualProposal}
                  </Button>
                </Box>

                <Collapse in={showManualProposalForm} timeout="auto" unmountOnExit>
                  <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f9fafb' }}>
                    <Typography variant="body2" fontWeight="medium" gutterBottom>
                      {copy.newManualProposal}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
                      {copy.manualProposalHelp}
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={copy.conceptId}
                          placeholder={copy.conceptIdPlaceholder}
                          value={manualProposalData.conceptId}
                          onChange={(e) => setManualProposalData({ ...manualProposalData, conceptId: e.target.value })}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={copy.conceptName}
                          placeholder={copy.conceptNamePlaceholder}
                          value={manualProposalData.conceptName}
                          onChange={(e) => setManualProposalData({ ...manualProposalData, conceptName: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={copy.vocabulary}
                          placeholder={copy.vocabularyPlaceholder}
                          value={manualProposalData.vocabulary}
                          onChange={(e) => setManualProposalData({ ...manualProposalData, vocabulary: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={copy.domain}
                          placeholder={copy.domainPlaceholder}
                          value={manualProposalData.domain}
                          onChange={(e) => setManualProposalData({ ...manualProposalData, domain: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          multiline
                          rows={3}
                          label={copy.justification}
                          placeholder={copy.justificationPlaceholder}
                          value={manualProposalData.justification}
                          onChange={(e) => setManualProposalData({ ...manualProposalData, justification: e.target.value })}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setShowManualProposalForm(false);
                              setManualProposalData({
                                conceptId: '',
                                conceptName: '',
                                vocabulary: '',
                                domain: '',
                                justification: ''
                              });
                            }}
                          >
                            {copy.cancel}
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleSubmitManualProposal}
                            disabled={!manualProposalData.conceptId.trim()}
                          >
                            {copy.submitProposal}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Collapse>
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Previous Feedback History - Only show if there's history */}
        {criticHistory.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HistoryIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight="bold">
                    {copy.previousImprovements}
                  </Typography>
                  <Chip label={criticHistory.length} size="small" color="primary" variant="outlined" />
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setShowCriticDialog(true)}
                >
                  {copy.showDetails}
                </Button>
              </Box>
              <Typography variant="body2" color="textSecondary">
                {copy.previousImprovementsSummary
                  .replace('{{count}}', criticHistory.length)
                  .replace('{{label}}', criticHistory.length === 1 ? copy.improvementSingle : copy.improvementPlural)}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Feedback Dialog */}
      <Dialog
        open={showCriticDialog}
        onClose={() => setShowCriticDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{copy.improveDialogTitle}</DialogTitle>
        <DialogContent>
          <OmopCriticAgentPanel
            criticHistory={criticHistory}
            onSubmitFeedback={handleCriticFeedback}
            isProcessing={isProcessing}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCriticDialog(false)}>{copy.close}</Button>
        </DialogActions>
      </Dialog>

      {/* Vocabulary Search Dialog */}
      <Dialog
        open={showVocabSearchDialog}
        onClose={() => setShowVocabSearchDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{copy.vocabularySearchTitle}</DialogTitle>
        <DialogContent>
          <OmopVocabularySearch
            onSelectConcept={handleManualMapping}
            parameterName={parameter?.name}
            isSearching={isProcessing}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowVocabSearchDialog(false)}>{copy.cancel}</Button>
        </DialogActions>
      </Dialog>

      {/* Info Dialog - Wie funktionieren KI-Vorschläge? */}
      <Dialog
        open={showInfoDialog}
        onClose={() => setShowInfoDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PsychologyIcon color="primary" />
            <Typography variant="h6">{copy.howAiWorks}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1" paragraph>
              {copy.aiIntro}
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                {copy.aiStep1Title}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                {copy.aiStep1Body}
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                {copy.aiStep2Title}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                {copy.aiStep2Body}
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                {copy.aiStep3Title}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                {copy.aiStep3Body}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              {copy.usageTips}
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 1 }}>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  {copy.tipHigh}
                </Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  {copy.tipMedium}
                </Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  {copy.tipLow}
                </Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body2">
                  {copy.tipPin}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              {copy.betterSuggestions}
            </Typography>
            <Typography variant="body2" paragraph>
              {copy.betterSuggestionsBody}
            </Typography>
            <Typography variant="caption" fontWeight="bold" display="block" gutterBottom>
              {copy.helpfulExamples}
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 0 }}>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body2">{exampleFeedback[0]}</Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body2">{exampleFeedback[1]}</Typography>
              </Box>
              <Box component="li" sx={{ mb: 1 }}>
                <Typography variant="body2">{exampleFeedback[2]}</Typography>
              </Box>
              <Box component="li" sx={{ mb: 0 }}>
                <Typography variant="body2">{exampleFeedback[3]}</Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowInfoDialog(false)}>{copy.close}</Button>
        </DialogActions>
      </Dialog>

      {/* Comments Dialog */}
      <Dialog
        open={showCommentsDialog}
        onClose={() => setShowCommentsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CommentIcon color="primary" />
            <Typography variant="h6">{copy.commentsTitle.replace('{{count}}', comments.length)}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {comments.length > 0 ? (
            <Box>
              {comments.map(comment => (
                <Paper key={comment.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {comment.author}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {comment.authorRole} • {new Date(comment.timestamp).toLocaleDateString(locale)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ThumbUpOutlinedIcon fontSize="small" />
                      <Typography variant="body2">{comment.likes}</Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2">{comment.text}</Typography>
                  {comment.replies.length > 0 && (
                    <Box sx={{ ml: 4, mt: 2 }}>
                      {comment.replies.map(reply => (
                        <Paper key={reply.id} variant="outlined" sx={{ p: 1.5, mb: 1, backgroundColor: '#f5f5f5' }}>
                          <Typography variant="caption" fontWeight="bold" display="block">
                            {reply.author}
                          </Typography>
                          <Typography variant="body2">{reply.text}</Typography>
                        </Paper>
                      ))}
                    </Box>
                  )}
                </Paper>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              {copy.noComments}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCommentsDialog(false)}>{copy.close}</Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog
        open={showHistoryDialog}
        onClose={() => setShowHistoryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            <Typography variant="h6">{copy.historyTitle}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {mappingHistory.length > 0 ? (
            <Box>
              {mappingHistory.map((entry, idx) => {
                const entryConcept = normalizeOmopConcept(entry?.concept || entry);

                return (
                  <Paper
                    key={idx}
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 2,
                      borderLeft: entry.isCurrent ? '4px solid #4caf50' : '4px solid #e0e0e0'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          v{entry.version} {entry.isCurrent && copy.currentVersion}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          {entryConcept.conceptName || copy.omopConcept}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" display="block">
                          {entryConcept.vocabulary} {entryConcept.conceptCode}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(entry.changedAt).toLocaleDateString(locale)}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" display="block" gutterBottom>
                      <strong>{copy.changedByLabel}</strong> {entry.changedBy}
                    </Typography>
                    <Typography variant="caption" display="block">
                      <strong>{copy.reasonLabel}</strong> {entry.changeJustification}
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              {copy.noHistory}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHistoryDialog(false)}>{copy.close}</Button>
        </DialogActions>
      </Dialog>

      {/* OMOP Info Dialog */}
      <Dialog
        open={showOmopInfoDialog}
        onClose={() => setShowOmopInfoDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="primary" />
          {copy.omopInfoTitle}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            <strong>OMOP (Observational Medical Outcomes Partnership)</strong> {omopInfo.intro.replace('OMOP (Observational Medical Outcomes Partnership) ', '')}
          </Typography>

          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            {copy.whyMap}
          </Typography>
          <Typography variant="body2" component="div">
            {omopInfo.whyMap.map((point, idx) => (
              <React.Fragment key={idx}>
                • {point}
                <br />
              </React.Fragment>
            ))}
          </Typography>

          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            {copy.conceptQuestion}
          </Typography>
          <Typography variant="body2">
            {omopInfo.concept}
          </Typography>

          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            {copy.yourRole}
          </Typography>
          <Typography variant="body2">
            {omopInfo.role}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowOmopInfoDialog(false)} variant="contained">
            {copy.understood}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Community Features Info Dialog */}
      <Dialog
        open={showCommunityInfoDialog}
        onClose={() => setShowCommunityInfoDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="primary" />
          {copy.communityRating}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {copy.communityIntro}
          </Typography>

          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, mb: 2 }}>
              <ThumbUpIcon color="success" sx={{ mt: 0.5 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  {copy.likesTitle}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {copy.likesBody}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, mb: 2 }}>
              <ThumbDownIcon color="error" sx={{ mt: 0.5 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  {copy.dislikesTitle}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {copy.dislikesBody}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
              <CommentIcon color="primary" sx={{ mt: 0.5 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  {copy.commentsBodyTitle}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {copy.commentsBody}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              {copy.communityTip}
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCommunityInfoDialog(false)} variant="contained">
            {copy.understood}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default OmopMappingDetail;
